import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

const ANIMATION_MS = 300

/**
 * Minimum pointer travel (px) before a touch is classified as a horizontal
 * close-drag vs. a vertical scroll. Below this, we stay undecided and let
 * native scrolling proceed.
 */
const DRAG_SLOP = 8

/** Fraction of the panel width a drag must pass to close on release. */
const CLOSE_THRESHOLD = 0.4

/**
 * A minimal right-side drawer for the filter panel. Deliberately does NOT use
 * vaul: vaul treats every touch inside a left/right drawer as a drag gesture,
 * which swallows native scrolling of the panel and its dropdowns. This is plain
 * overlay + panel with native `overflow` scrolling, so the body and any portalled
 * menus scroll normally on touch.
 *
 * It slides in/out for a user-initiated open or close. When `animate` is false it
 * appears/disappears instantly — used when the drawer is *restored* after a
 * filter selection navigates and remounts the sidebar: there's nothing to
 * animate (it was already open), and skipping the slide lets the anchored filter
 * dropdowns measure their final position immediately instead of chasing a moving
 * panel. Mobile only — `md:hidden` keeps it off desktop, where the sidebar is
 * always visible. Closes on overlay click, Escape, any caller control, or a
 * rightward swipe on the panel.
 *
 * The swipe gesture deliberately stays out of the way of native behaviour: a
 * touch is only "captured" as a close-drag once it moves decisively rightward
 * (`deltaX` beyond `deltaY` and past `DRAG_SLOP`). Anything else — a vertical
 * scroll, a tap on a dropdown, an interaction with a range slider — falls
 * through untouched. That is the whole reason this component avoids vaul (see
 * above); reintroducing a grabby gesture layer would defeat the point.
 */
export default function FilterDrawer({ open, onClose, animate = true, children }) {
    // `mounted` keeps the panel in the DOM through an exit animation; `shown`
    // drives the slide (translate-x-0 when true, translate-x-full when false).
    const [mounted, setMounted] = useState(open)
    const [shown, setShown] = useState(open)

    const panelRef = useRef(null)
    const overlayRef = useRef(null)
    // Live swipe state, kept in a ref so touchmove writes to the DOM directly
    // without re-rendering every frame.
    const drag = useRef({ startX: 0, startY: 0, width: 0, decided: false, dragging: false, delta: 0 })

    useEffect(() => {
        if (open) {
            setMounted(true)
            return
        }
        if (!animate) {
            setShown(false)
            setMounted(false)
            return
        }
        setShown(false)
        const timer = setTimeout(() => setMounted(false), ANIMATION_MS)
        return () => clearTimeout(timer)
    }, [open, animate])

    // Once mounted for an open, flip `shown` to trigger the slide. A double rAF
    // guarantees the off-screen frame paints first so the transition actually
    // runs; when not animating, show immediately.
    useEffect(() => {
        if (!mounted || !open) {
            return
        }
        if (!animate) {
            setShown(true)
            return
        }
        let inner
        const outer = requestAnimationFrame(() => {
            inner = requestAnimationFrame(() => setShown(true))
        })
        return () => {
            cancelAnimationFrame(outer)
            cancelAnimationFrame(inner)
        }
    }, [mounted, open, animate])

    useEffect(() => {
        if (!mounted) {
            return
        }
        const onKey = (event) => {
            if (event.key === "Escape") {
                onClose()
            }
        }
        document.addEventListener("keydown", onKey)
        return () => document.removeEventListener("keydown", onKey)
    }, [mounted, onClose])

    // Lock background scroll while mounted. `overflow: hidden` on the body does
    // not affect the drawer's own scrollable regions (they scroll independently).
    useEffect(() => {
        if (!mounted) {
            return
        }
        const previous = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.body.style.overflow = previous
        }
    }, [mounted])

    // Suppress the CSS transition so the panel/overlay track the finger 1:1
    // during a drag; restored on release so the snap-back or close animates.
    const setDragTransition = (value) => {
        if (panelRef.current) {
            panelRef.current.style.transition = value
        }
        if (overlayRef.current) {
            overlayRef.current.style.transition = value
        }
    }

    // Move the panel to `x` px off its resting (open) position and dim the
    // overlay proportionally.
    const paintDrag = (x) => {
        if (panelRef.current) {
            panelRef.current.style.transform = `translateX(${x}px)`
        }
        if (overlayRef.current && drag.current.width) {
            overlayRef.current.style.opacity = String(1 - x / drag.current.width)
        }
    }

    // Hand control back to the className-driven transforms.
    const clearInlineDrag = () => {
        if (panelRef.current) {
            panelRef.current.style.transform = ""
        }
        if (overlayRef.current) {
            overlayRef.current.style.opacity = ""
        }
    }

    const onTouchStart = (event) => {
        // Let controls that own the horizontal axis (range sliders, carousels)
        // keep their gestures.
        if (event.target.closest('input[type="range"], [role="slider"], [data-no-drawer-swipe]')) {
            return
        }
        const touch = event.touches[0]
        drag.current = {
            startX: touch.clientX,
            startY: touch.clientY,
            width: panelRef.current ? panelRef.current.offsetWidth : 0,
            decided: false,
            dragging: false,
            delta: 0,
        }
    }

    const onTouchMove = (event) => {
        const state = drag.current
        if (!state.width) {
            return
        }
        const touch = event.touches[0]
        const dx = touch.clientX - state.startX
        const dy = touch.clientY - state.startY

        if (!state.decided) {
            if (Math.abs(dx) < DRAG_SLOP && Math.abs(dy) < DRAG_SLOP) {
                return
            }
            // Rightward and more horizontal than vertical → close-drag.
            // Otherwise this touch belongs to native scrolling; ignore it.
            state.decided = true
            state.dragging = dx > 0 && dx > Math.abs(dy)
            if (state.dragging) {
                setDragTransition("none")
            }
        }

        if (state.dragging) {
            state.delta = Math.max(0, dx)
            paintDrag(state.delta)
        }
    }

    const endDrag = () => {
        const state = drag.current
        if (!state.dragging) {
            return
        }
        state.dragging = false
        state.decided = false
        setDragTransition("")

        if (state.delta > state.width * CLOSE_THRESHOLD) {
            // Continue the motion fully off-screen, then let the caller unmount.
            paintDrag(state.width)
            onClose()
        } else {
            clearInlineDrag()
        }
    }

    if (!mounted || typeof document === "undefined") {
        return null
    }

    return createPortal(
        <div className="fixed inset-0 z-[100] md:hidden">
            <div
                ref={overlayRef}
                onClick={onClose}
                className={`absolute inset-0 bg-black/60 ${
                    animate ? "transition-opacity duration-300" : ""
                } ${shown ? "opacity-100" : "opacity-0"}`}
            />

            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label="Filters"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={endDrag}
                onTouchCancel={endDrag}
                className={`absolute inset-y-0 right-0 flex w-[85%] max-w-sm flex-col bg-white shadow-xl ${
                    animate ? "transition-transform duration-300 ease-out" : ""
                } ${shown ? "translate-x-0" : "translate-x-full"}`}
            >
                {children}
            </div>
        </div>,
        document.body
    )
}
